import React from 'react'

import Stack from '@mui/material/Stack'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'

import UserIcon from 'src/components/ui/UserIcon'
import {
  ISchemaInfo,
  useStorageObjectsStore,
} from 'src/hooks/useStorageObjects'
import { useSearchQuery } from 'src/hooks/useSearchQuery'
import { useLayoutContext } from 'src/layouts/VerticalLayout'
import useResponsive from 'src/hooks/useResponsive'

export interface IObjectViewNavigationProps {
  scheme: ISchemaInfo
  navHover?: boolean
  navVisible?: boolean
}
export const ObjectViewNavigation = (props: IObjectViewNavigationProps) => {
  const { scheme } = props
  const schemeName = scheme.identifier
  const { setNavHandle } = useLayoutContext()
  const { isMobile } = useResponsive()

  const { useSelect, useAction } = useStorageObjectsStore({
    prefix: 'primary',
    schemeName: scheme.identifier,
  })

  const getSmartListItemDefault = useAction.getSmartListItemDefault()
  const getSmartListItemDefaultStatus =
    useAction.getSmartListItemDefault.status()
  const getFieldsGroupsStatus = useAction.getFieldsGroups.status()
  const getFieldsGroups = useAction.getFieldsGroups()
  const fieldsShemes = useSelect.fieldsShemes()

  const boardList = useSelect.boardList()
  const { query, setPathQuery } = useSearchQuery()

  React.useEffect(() => {
    if (getSmartListItemDefaultStatus.isClear) {
      getSmartListItemDefault({})
    }
  }, [getSmartListItemDefaultStatus.isClear])

  React.useEffect(() => {
    if (getFieldsGroupsStatus.isClear || fieldsShemes.length) {
      getFieldsGroups({ limit: 50 })
    }
  }, [getFieldsGroupsStatus.isClear, fieldsShemes.length])

  if (!boardList || boardList?.length === 0) {
    if (getFieldsGroupsStatus.isPending) {
      return (
        <Stack
          onClick={(event) => event.stopPropagation()}
          sx={{ fontSize: isMobile ? '1rem' : undefined }}
        >
          <List dense={!isMobile} disablePadding={!isMobile}>
            <ObjectViewSkeleton count={1} />
          </List>
        </Stack>
      )
    }

    return (
      <Stack
        onClick={(event) => event.stopPropagation()}
        sx={{ fontSize: isMobile ? '1rem' : undefined }}
      >
        <List dense={!isMobile} disablePadding={true}>
          <Stack px={5} alignItems="flex-start">
            <Stack
              direction="row"
              columnGap={3}
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
            >
              <UserIcon
                icon="fluent:grid-kanban-16-filled"
                fontSize={20}
                color="inherit"
              />
              <Typography
                variant="body2"
                fontSize={12}
                sx={{
                  color: 'grey.500',
                }}
              >
                No boards available
              </Typography>
            </Stack>
          </Stack>
        </List>
      </Stack>
    )
  }

  return (
    <Stack
      onClick={(event) => event.stopPropagation()}
      sx={{ fontSize: isMobile ? '1rem' : undefined }}
    >
      <List dense={!isMobile} disablePadding={isMobile}>
        <ListItemButton
          key={'table'}
          dense={!isMobile}
          sx={{
            color:
              query.objectsViewType === 'table'
                ? 'primary.main'
                : 'common.white',
          }}
          onClick={() => {
            setPathQuery(`storage/${schemeName}`, {
              objectsViewType: 'table',
            })
          }}
        >
          <ListItemIcon sx={{ color: 'inherit !important' }}>
            <UserIcon color="inherit" icon={'fluent:table-32-filled'} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                fontSize={12}
                sx={{ color: 'inherit' }}
              >{`Table`}</Typography>
            }
          />
        </ListItemButton>

        {boardList.map((board) => (
          <ListItemButton
            key={board.identifier}
            dense={!isMobile}
            sx={{
              color:
                query.objectsViewType === 'board' &&
                board.identifier === query.objectsViewId
                  ? 'primary.main'
                  : 'common.white',

              '&:hover': {
                color: 'primary.main',
              },
            }}
            onClick={() => {
              if (isMobile) {
                setNavHandle?.()
              }
              setPathQuery(`storage/${schemeName}`, {
                objectsViewType: 'board',
                objectsViewId: board.identifier,
              })
            }}
          >
            <ListItemIcon sx={{ color: 'inherit !important' }}>
              <UserIcon color="inherit" icon={'fluent:grid-kanban-16-filled'} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  noWrap
                  fontSize={12}
                  color="inherit"
                >{`Board ${board.title}`}</Typography>
              }
            />
          </ListItemButton>
        ))}
      </List>
    </Stack>
  )
}

function ObjectViewSkeleton(props: { count?: number }) {
  const { count = 2 } = props

  return (
    <>
      {new Array(count).fill(null).map((_, index) => (
        <ListItemButton key={index} disabled>
          <ListItemIcon>
            <Skeleton
              width={22}
              height={22}
              variant="rounded"
              sx={{ backgroundColor: 'grey.50' }}
            />
          </ListItemIcon>
          <ListItemText>
            <Skeleton
              width={index % 2 ? 80 : 100}
              variant="rounded"
              sx={{ backgroundColor: 'grey.50', height: '12px ' }}
            />
          </ListItemText>
        </ListItemButton>
      ))}
    </>
  )
}
